import { runInAction } from 'mobx'
import { useState, type ReactNode } from 'react'

import { userManager } from '@/Core/Managers'
import { notify } from '@/Ship/Helpers/notify'
import { parseApiErrorDescription } from '@/Ship/Helpers/parseApiError'
import { resolveUserPhotoUrl } from '@/Ship/Helpers/userPhoto'
import { SaveUserPhotoTask, UploadUserPhotoTask } from '@/Ship/Tasks/UserPhotoTasks'

import { usePhotoCropPicker } from './usePhotoCropPicker'

export interface UserPhotoEditor {
    /** Відкрити вибір файлу */
    pick: () => void
    /** Взяти файл (з drag-and-drop або вибору): перевірка → вікно обрізання */
    accept: (file: File | undefined | null) => Promise<void>
    /** Прибрати фото */
    remove: () => Promise<void>
    /** Йде збереження або видалення */
    busy: boolean
    /** Чи є зараз фото в поточного користувача */
    hasPhoto: boolean
    /** Прихований input і вікно обрізання — рендерити один раз поруч із тригером */
    holder: ReactNode
}

const mergeUser = (patch: Record<string, unknown>) => {
    runInAction(() => {
        userManager.user = { ...(userManager.user as any), ...patch }
    })
}

/**
 * Зміна фото поточного користувача: вибір або перетягування файлу, перевірка
 * (JPG/PNG/WEBP до 15 МБ, HEIC з iPhone — через JPEG), обрізання в круглий кадр, завантаження квадрата 512×512
 * і PATCH профілю. Після збереження оновлює userManager.user, тож фото одразу
 * з'являється в меню та на сторінці профілю.
 */
export const useUserPhotoEditor = (): UserPhotoEditor => {
    const [removing, setRemoving] = useState(false)

    const user = (userManager.user ?? {}) as Record<string, any>
    const hasPhoto = resolveUserPhotoUrl(user['photo']) !== null

    const userUuid = (): string | null => {
        const uuid = (userManager.user as any)?.uuid
        if (typeof uuid === 'string' && uuid !== '') return uuid
        notify.error('Не вдалося визначити користувача')
        return null
    }

    const picker = usePhotoCropPicker({
        inputTestId: 'user-photo-input',
        onSave: async (blob) => {
            const uuid = userUuid()
            if (!uuid) return false
            try {
                const photo = await UploadUserPhotoTask(blob, 'avatar.jpg')
                const updated = await SaveUserPhotoTask(uuid, photo['@id'])
                mergeUser({ ...updated, photo: updated?.['photo'] ?? photo })
                notify.success('Фото оновлено')
                return true
            } catch (error) {
                const description = await parseApiErrorDescription(error, '')
                notify.error('Не вдалося зберегти фото', description ? { description } : undefined)
                return false
            }
        },
    })

    const remove = async () => {
        const uuid = userUuid()
        if (!uuid) return
        setRemoving(true)
        try {
            const updated = await SaveUserPhotoTask(uuid, null)
            mergeUser({ ...updated, photo: null })
            notify.success('Фото видалено')
        } catch (error) {
            const description = await parseApiErrorDescription(error, '')
            notify.error('Не вдалося видалити фото', description ? { description } : undefined)
        } finally {
            setRemoving(false)
        }
    }

    return {
        pick: picker.pick,
        accept: picker.accept,
        remove,
        busy: picker.busy || removing,
        hasPhoto,
        holder: picker.holder,
    }
}
